import { registerSW } from 'virtual:pwa-register';

/**
 * Registro del service worker con comprobación PERIÓDICA de nueva versión.
 *
 * El registro que inyecta vite-plugin-pwa por defecto sólo mira si hay una
 * versión nueva en el evento `load` de la página. Como la sesión dura un año,
 * mucha gente deja la pestaña abierta días enteros: sin recargar nunca, esa
 * pestaña jamás se entera de un despliegue y sigue ejecutando el bundle
 * antiguo (de ahí el clásico "hazte un Ctrl+R").
 *
 * Con `registerType: 'autoUpdate'`, en cuanto el service worker nuevo toma el
 * control la página se recarga sola: el usuario no tiene que hacer nada.
 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Lo mínimo que necesitamos de un ServiceWorkerRegistration. */
interface Updatable {
  update: () => Promise<unknown>;
}

/**
 * Programa las comprobaciones de versión: cada `intervalMs` y además cada vez
 * que la pestaña vuelve a primer plano — el momento típico en que alguien
 * retoma la app por la mañana tras un despliegue nocturno.
 */
export function scheduleUpdateChecks(
  registration: Updatable,
  intervalMs: number = CHECK_INTERVAL_MS,
): void {
  const check = () => {
    // `update()` va contra la red saltándose la caché del service worker.
    void Promise.resolve(registration.update()).catch(() => {
      // Sin red o servidor caído: se reintenta en el siguiente ciclo.
    });
  };

  setInterval(check, intervalMs);
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}

export function initServiceWorkerUpdates(): void {
  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (registration) scheduleUpdateChecks(registration);
    },
  });
}
